import { Component, ErrorInfo, ReactNode } from 'react'
import { AlertCircle } from 'lucide-react'
import { Button } from '@brent/core-ui/ui'
import { AwsRumContextType } from './AwsRumProvider'
import { AwsRum } from 'aws-rum-web'

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@brent/core-ui/ui'
import React from 'react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
}

/**
 * Error Boundary component to catch React errors and display a fallback UI
 *
 * This component wraps the entire application to catch any unhandled errors
 * in the component tree and display a user-friendly error message instead of
 * the white screen of death.
 *
 * @see https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */
export class ErrorBoundary extends Component<Props, State> {
  static contextType = React.createContext<AwsRumContextType>(null)

  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('Error caught by ErrorBoundary:', error, errorInfo)
    }

    const awsRum = this.context as AwsRum
    if (awsRum) {
      awsRum.recordError(error)
    }

    this.setState({ errorInfo })
  }

  handleReload = () => {
    // Reload the page to reset the application state
    window.location.reload()
  }

  handleGoHome = () => {
    // Navigate to home page and reset error state
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="rounded-full bg-destructive/10 p-3">
                  <AlertCircle className="h-6 w-6 text-destructive" />
                </div>
                <div>
                  <CardTitle>Something went wrong</CardTitle>
                  <CardDescription>
                    An unexpected error occurred in the application.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="rounded-lg bg-muted p-4">
                <p className="text-sm font-medium text-muted-foreground mb-2">Error Details:</p>
                <p className="text-sm font-mono text-foreground">
                  {this.state.error?.message || 'Unknown error'}
                </p>
              </div>

              {import.meta.env.DEV && this.state.errorInfo && (
                <details className="rounded-lg bg-muted p-4">
                  <summary className="text-sm font-medium text-muted-foreground cursor-pointer mb-2">
                    Stack Trace (Development Only)
                  </summary>
                  <pre className="text-xs font-mono text-foreground overflow-auto max-h-64 mt-2">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}

              <div className="text-sm text-muted-foreground space-y-2">
                <p>This error has been logged. You can try:</p>
                <ul className="list-disc list-inside ml-2 space-y-1">
                  <li>Reloading the page to reset the application</li>
                  <li>Going back to the home page</li>
                  <li>Clearing your browser cache and cookies</li>
                  <li>Contacting support if the problem persists</li>
                </ul>
              </div>
            </CardContent>

            <CardFooter className="flex gap-3">
              <Button onClick={this.handleReload} variant="default">
                Reload Page
              </Button>
              <Button onClick={this.handleGoHome} variant="outline">
                Go to Home
              </Button>
            </CardFooter>
          </Card>
        </div>
      )
    }

    return this.props.children
  }
}
